import {
  DvFilterItem,
  DvGridComponent,
  DvQuickFilterComponent,
  DvToolbarAction,
} from '@devinco/ui/grid';
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  effect,
  inject,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { Entity } from '@shared/constants/entity';
import { TranslateService } from '@ngx-translate/core';
import { FleetFilteredItemsService } from '@pages/fleet/services/fleet-filtered-items.service';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import {
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
  IRowNode,
  RowDataUpdatedEvent,
} from 'ag-grid-community';
import { FleetGridService } from './fleet-grid.service';
import { DirectivesModule } from '@shared/directives/directives.module';
import { DvSplitterEventsService } from '@devinco/ui/dds/splitter';
import { VtSplitterService } from '@shared/services/grid/vt-splitter.service';
import { FleetDto } from '@pages/fleet/interfaces';
import { HistoricalPositionService } from '@pages/fleet/services/historical-position-service';

@Component({
  imports: [
    DvGridComponent,
    DvQuickFilterComponent,
    AgGridModule,
    DirectivesModule,
  ],
  selector: 'app-fleet-grid',
  templateUrl: './fleet-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [GridOptionsService, FleetGridService],
})
export class FleetGridComponent implements OnInit, OnDestroy {
  private gridOptionsService = inject(GridOptionsService);
  private gridService = inject(FleetGridService);
  private translate = inject(TranslateService);
  private fleetFilteredItemsService = inject(FleetFilteredItemsService);
  private splitterService = inject(VtSplitterService);
  private splitterEvents = inject(DvSplitterEventsService);
  private historicalPositionService = inject(HistoricalPositionService);

  gridOptions: GridOptions;
  quickFilters: DvFilterItem[];
  actions: DvToolbarAction[];
  alwaysShowFilterButton = false;

  @Output() filtersCleared = new EventEmitter<void>();
  private agGrid = viewChild<AgGridAngular>('agGrid');

  gridApi: GridApi | undefined;

  constructor() {
    effect(() => {
      const agGrid = this.agGrid();
      if (agGrid) {
        this.splitterService.agGrid = agGrid;
      }
    });
  }

  ngOnInit(): void {
    this.quickFilters = this.getQuickFilters();
    this.actions = this.gridService.getGridActions();
    this.gridOptions = {
      ...this.gridOptionsService.clientSide(
        this.gridService.colDefs,
        Entity.Fleet
      ),
      rowSelection: {
        mode: 'multiRow',
        enableClickSelection: false,
        headerCheckbox: false,
        checkboxes: false,
        selectAll: 'filtered',
      },
      suppressDragLeaveHidesColumns: true,
    };

    this.splitterEvents.isSplitterOpened$.next(true);
  }

  ngOnDestroy(): void {
    this.fleetFilteredItemsService.fleetGridFilterChanged$.next([]);
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
  }

  onFilterChanged(event: FilterChangedEvent<FleetDto>): void {
    this.fleetFilteredItemsService.fleetGridFilterChanged$.next(
      this.getAndSelectFilteredNodes(event.api).map(node => node.data!, true)
    );

    this.alwaysShowFilterButton =
      event.api.getDisplayedRowCount() !==
      this.fleetFilteredItemsService.fleetGridFilterChanged$.getValue().length;

    if (this.historicalPositionService.selectedHistoricalDateTime()) {
      this.historicalPositionService.historicalPositionFetchTrigger$.next(
        this.historicalPositionService.selectedHistoricalDateTime()
      );
    }
  }

  onFiltersCleared(): void {
    this.gridApi?.deselectAll();
    this.filtersCleared.emit();
  }

  onRowDataUpdated(event: RowDataUpdatedEvent): void {
    this.fleetFilteredItemsService.fleetGridFilterChanged$.next(
      this.getAndSelectFilteredNodes(event.api, true).map(node => node.data!)
    );
  }

  onFirstDataRendered(event: FirstDataRenderedEvent): void {
    this.fleetFilteredItemsService.fleetGridFilterChanged$.next(
      this.getAndSelectFilteredNodes(event.api).map(node => node.data!)
    );
  }

  private getAndSelectFilteredNodes(
    api: GridApi<FleetDto>,
    disableSelect = false
  ): IRowNode<FleetDto>[] {
    const filteredNodes: IRowNode<FleetDto>[] = [];
    api.forEachNodeAfterFilter(node => {
      if (!disableSelect) {
        node.setSelected(true);
      }
      filteredNodes.push(node);
    });

    return filteredNodes;
  }

  private getQuickFilters(): DvFilterItem[] {
    return [
      {
        defaultActive: true,
        title: this.translate.instant('Fleet.All'),
        gridFilters: {},
        suppressDeselect: true,
      },
      {
        title: this.translate.instant('Fleet.Vehicle'),
        gridFilters: {
          AssetID: { filterType: 'set', values: ['vehicle'] },
        },
        suppressDeselect: true,
      },
      {
        title: this.translate.instant('Fleet.Asset'),
        gridFilters: {
          AssetID: { filterType: 'set', values: ['asset'] },
        },
        suppressDeselect: true,
      },
    ];
  }
}
